import { useMemo, useState } from "react"

import type { DeviceRowInfo, DeviceRowsByInstallationStatus } from "types/device.types"

export type InstallationTab = "installed" | "notInstalled" | "all"

export interface DeviceInstallationTabsCounts {
  installed: number
  notInstalled: number
  all: number
}

export interface DeviceInstallationTabsState {
  activeTab: InstallationTab
  setActiveTab: (tab: InstallationTab) => void
  filteredRows: DeviceRowInfo[]
  counts: DeviceInstallationTabsCounts
}

/**
 * Manages tab-based filtering of device rows by installation status.
 *
 * - "installed": only devices installed on a span or tower.
 * - "notInstalled": every other device, including those missing their span or tower.
 * - "all": all devices regardless of installation status.
 *
 * Default active tab is "installed".
 */
export function useDeviceInstallationTabs(
  devicesByInstallationStatus: DeviceRowsByInstallationStatus,
): DeviceInstallationTabsState {
  const [activeTab, setActiveTab] = useState<InstallationTab>("installed")

  const counts = useMemo(
    (): DeviceInstallationTabsCounts => ({
      installed: devicesByInstallationStatus.installed.length,
      notInstalled: devicesByInstallationStatus.notInstalled.length,
      all: devicesByInstallationStatus.all.length,
    }),
    [devicesByInstallationStatus],
  )

  const filteredRows = useMemo(
    (): DeviceRowInfo[] => devicesByInstallationStatus[activeTab],
    [activeTab, devicesByInstallationStatus],
  )

  return { activeTab, setActiveTab, filteredRows, counts }
}
